import React, { useEffect, useState } from "react";
import { Button, Carousel, Modal, Badge } from "flowbite-react";
import { useParams } from "react-router-dom";
import { PiShareDuotone } from "react-icons/pi";

import carouseltheme from "../../themes/carousel.json";
import api from "../../helpers/Api";
import Loading from "../../components/Loading";
import MessageError from "../../components/MessageError";
import Ticket from "../../components/Ticket";
import moment from "moment";
import "moment/locale/es";
import toast from "react-hot-toast";
moment.locale("es");

function ModalTransfer({ openModal, setOpenModal, userEmail, setUserEmail, transferTicket, isLoading }) {
    return (
        <Modal show={openModal} size="md" onClose={() => setOpenModal(false)} popup>
            <Modal.Header className="bg-black text-white flex justify-center items-center">
                <img src="/identity/MONOLOGO.png" className="h-6 sm:h-9" alt="Monoticket" />
            </Modal.Header>

            <Modal.Body className="bg-[#0f0e17] text-white space-y-4">
                <div className="text-center">
                    <span className="text-sm">Escribe el correo de quien recibirá el boleto</span>
                </div>

                <div>
                    <input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="Correo electrónico"
                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5"
                    />
                </div>

                <div className="text-center">
                    <p className="text-xs text-red-400">
                        El boleto transferido no podrá recuperarse
                    </p>
                    <p className="text-xs">
                        Solo es posible transferir boletos a usuarios registrados
                    </p>
                </div>

                <div className="flex flex-col gap-2">
                    <Button onClick={transferTicket} className={`event-one__btn thm-btn p-2 m-2 ${isLoading ? "thm-btn__disabled" : ""}`}>
                        {isLoading ? "Transfiriendo..." : "Transferir"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export function Render({ ticketData, setShouldRefresh }) {
    const [openModal, setOpenModal] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [ticketSelected, setTicketSelected] = useState(null);
    const [isLoading, setIsLoading] = useState(false)

    const transferTicket = async () => {
        try {
            setIsLoading(true)
            if (!ticketSelected) return;

            const email = (userEmail || "").trim();
            if (!email) {
                toast.error("Escribe un correo válido para transferir el boleto.");
                setIsLoading(false)
                return;
            }

            const dataTransfer = {
                email,
                ticketid: ticketSelected.id,
                eventId: ticketSelected.event.event_id
            };

            const responseTransfer = await api.post("Ticket/Transfer", dataTransfer);

            if (responseTransfer.status !== 200) {
                setIsLoading(false)
                toast.error(responseTransfer.data?.error || "No se pudo transferir el boleto.");
                return;
            }

            toast.success(responseTransfer.data?.message || "Boleto transferido correctamente.");
            setOpenModal(false);
            setIsLoading(false)

            setShouldRefresh((v) => !v);
        } catch (err) {
            toast.error(err?.response?.data?.error || "Error transfiriendo el boleto.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-6 max-w-6xl h-full mx-auto">
            <ModalTransfer
                openModal={openModal}
                setOpenModal={setOpenModal}
                userEmail={userEmail}
                setUserEmail={setUserEmail}
                transferTicket={transferTicket}
                isLoading={isLoading}
            />

            <Carousel theme={carouseltheme} slide={false} indicators pauseOnHover className="h-[980px]">
                {ticketData.map((ticket, index) => {
                    const isTransfer = String(ticket.transactionId || "").includes("Transfer");

                    return (
                        <div key={`${ticket.hash || ticket.number_ticket || index}`} className="flex flex-col items-center">
                            <div className="text-center text-sm text-white mb-3">
                                {index + 1} de {ticketData.length}
                            </div>

                            <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-sm mb-3">
                                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white/70 backdrop-blur px-3 py-2 shadow-sm">
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-slate-900">Tu boleto</span>
                                        <span className="text-xs text-slate-600">
                                            Presenta el siguiente boleto en la entrada
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isTransfer ? (
                                            <Badge color="gray">Transferido</Badge>
                                        ) : (
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setTicketSelected(ticket);
                                                    setUserEmail("");
                                                    setOpenModal(true);
                                                }}
                                                className={`event-one__btn thm-btn ${isLoading ? "thm-btn__disabled" : ""}`}
                                            >
                                                <PiShareDuotone size={18} className="mr-2" />
                                                Transferir
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <Ticket ticketData={ticket} />
                        </div>
                    );
                })}
            </Carousel>
        </div>
    );
}

export default function MyTicketDetails() {
    const { purchaseId } = useParams();
    const [ticketData, setTicketData] = useState(null);
    const [error, setError] = useState(false);
    const [shouldRefresh, setShouldRefresh] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const ticketDataResponse = await api.get(`/Ticket/Details/ByCustomer?purchaseId=${purchaseId}`);
                setTicketData(ticketDataResponse.data);
            } catch (error) {
                console.error("Error fetching ticketData data:", error);
                setError(true);
            }
        };

        fetchData();
    }, [purchaseId, shouldRefresh]);

    if (error) {
        return (
            <MessageError
                title="Ocurrió un error al cargar tus boletos."
                description="Por favor, contacta soporte técnico o consulta tu cuenta para verlos."
            />
        );
    }

    if (!ticketData) return <Loading text={"Cargando boletos..."} />;

    return <Render ticketData={ticketData} setShouldRefresh={setShouldRefresh} />;
}
